import { type Dirent, readdirSync, readFileSync, statSync } from 'node:fs';
import { join, relative, sep } from 'node:path';
import { createIgnore } from './ignore.js';

/** Skip files larger than this (generated bundles, data dumps). */
const MAX_FILE_BYTES = 512 * 1024;
/** How many leading chars to scan for a NUL byte (the binary signal). */
const BINARY_SNIFF = 8000;

/**
 * True when decoded text looks binary — a meaningful FRACTION of NUL bytes in the
 * first {@link BINARY_SNIFF} chars. A stray NUL in real source (e.g. a sentinel
 * string literal) must not exclude the file; genuine binaries are NUL-dense.
 */
function looksBinary(text: string): boolean {
  const limit = Math.min(text.length, BINARY_SNIFF);
  if (limit === 0) return false;
  let nul = 0;
  for (let i = 0; i < limit; i++) {
    if (text.charCodeAt(i) === 0) nul += 1;
  }
  return nul / limit > 0.01;
}

/**
 * Decode a file buffer to text, honoring a UTF-16 / UTF-8 BOM so UTF-16-encoded
 * source (common on Windows) is NOT mistaken for binary. Returns null only for
 * genuinely binary content — a NUL byte surviving a plain UTF-8 decode.
 */
function decodeText(buf: Buffer): string | null {
  let text: string;
  if (buf.length >= 2 && buf[0] === 0xff && buf[1] === 0xfe) {
    text = buf.toString('utf16le'); // UTF-16 LE
  } else if (buf.length >= 2 && buf[0] === 0xfe && buf[1] === 0xff && buf.length % 2 === 0) {
    const swapped = Buffer.from(buf); // UTF-16 BE → swap to LE
    swapped.swap16();
    text = swapped.toString('utf16le');
  } else if (buf.length >= 3 && buf[0] === 0xef && buf[1] === 0xbb && buf[2] === 0xbf) {
    text = buf.subarray(3).toString('utf8'); // UTF-8 BOM
  } else {
    text = buf.toString('utf8');
    if (looksBinary(text)) return null;
  }
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text; // strip a leading BOM
}

export interface ScannedFile {
  /** Repo-relative, POSIX path. */
  path: string;
  content: string;
}

/** Walk the repo, honoring ignore rules, returning readable text files (UTF-8/UTF-16). */
export function scanRepo(root: string): ScannedFile[] {
  const isIgnored = createIgnore(root);
  const out: ScannedFile[] = [];

  const walk = (dir: string): void => {
    let entries: Dirent[];
    try {
      entries = readdirSync(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (const entry of entries) {
      const abs = join(dir, entry.name);
      const rel = relative(root, abs).split(sep).join('/');
      if (!rel || isIgnored(rel, entry.isDirectory())) continue;
      if (entry.isDirectory()) {
        walk(abs);
      } else if (entry.isFile()) {
        try {
          if (statSync(abs).size > MAX_FILE_BYTES) continue;
          const content = decodeText(readFileSync(abs));
          if (content === null) continue; // genuinely binary
          out.push({ path: rel, content });
        } catch {
          /* unreadable — skip */
        }
      }
    }
  };

  walk(root);
  return out;
}
